import { ApiError, path } from 'utils'
import z from 'zod/v4'

import { OperationLevelPermission } from '../auth-claims/permissions'
import { rateZoneFeatureCollection } from '../geojson-schemas'
import { defineApi } from '../http'
import { layerScopedPathSchema } from './shared'

export const contourApi = defineApi({
  pathSchema: layerScopedPathSchema,
  querySchema: z.object({
    /**
     * The minimum size (in acres) for a feature to be included in the output.  Features smaller than this will be ignored.
     *
     * default 0.01
     */
    minFeature: z.number().optional(),
    /**
     * controls how jagged the contour is... the larger the value, the fewer points that are used to construct it
     * used to compute a threshold for deciding if approximations are acceptable. And the threshold is epsilon * the contour perimeter.  So that means, an epsilon of 0.005 asks whether an approximation is less than 0.5% of the contour’s perimeter away from the original value.
     *
     * default 0.5
     */
    epsilon: z.number().optional(),
    /**
     * effectively the minimum feature size.  generally, regions must be at least this size to be bounded or holes must be this size to be considered an actual hole
     *
     * default 10
     */
    tolerance: z.number().optional(),
    /**
     * The minimum value to include in the contouring process.  Values below this will be ignored.
     */
    min: z.number().optional(),
    /**
     * The maximum value to include in the contouring process.  Values above this will be ignored.
     */
    max: z.number().optional(),
    /**
     * zoom level on which at which the map is being manipulated internally by the algorithm.  this should be automatically determined in the future.
     * default 20
     */
    zoom: z.number().optional()
  }),
  pathFor: (p) => {
    const { requestType, orgCode, streamId, applicationId, layerId, ...queryParams } = p
    return path(
      `${requestType}/streams/${orgCode}/${streamId}/layers/${applicationId}/${layerId}/contour`,
      {
        ...queryParams,
        epsilon: p.epsilon ?? 0.005,
        tolerance: p.tolerance ?? 1
      }
    )
  },
  name: 'GetLayerContours',
  description:
    'Generate contour lines (isolines) for a layer to visualize data gradients on the map',
  requiredPermissions: OperationLevelPermission.ViewApplicationData,
  errorMap: (error) => {
    if (error instanceof ApiError && error.status === 413) {
      return new Error(
        'Field extents too large, possibly due to product dropped while on the road between fields. We are working on a solution to this, but in the meantime, you can try a different map layer.'
      )
    }
    return error
  },
  responseSchema: rateZoneFeatureCollection
})
